import { useEffect, useMemo, useState } from "react";
import { endOfDay, format, startOfDay } from "date-fns";
import { BarChart3 } from "lucide-react";
import { LogoLoadingSpinner } from "~/components/LoadingSpinner";
import { SelectInput } from "~/components/SelectInput";
import {
    ApiQueryEndpoints,
    QueryResponse,
    API_QUERY_BASE_URLS,
} from "~/modules/filters/types";
import {
    AggregateItem,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { TOKEN_TIMESERIES_GROUP_BY_LABELS } from "~/modules/token_timeseries/consts";
import { NetFlowsChart } from "~/modules/visualization/components/NetFlowsChart/component";
import { getChartHeight } from "~/modules/flows_explorer/pages/list/utils";
import { api } from "~/utils/api";
import { deobfuscate } from "~/utils/obfuscate";
import { formatCurrencyValueCompact } from "~/utils/functions/formatCurrencyValueCompact";
import { ChartConfig } from "~/modules/charts/types";
import {
    DEFAULT_NET_FLOWS_GROUP_BY,
    NET_FLOWS_MAX_ASSETS,
    getNetFlowsApiFilters,
    toNetFlowsChartConfig,
} from "../ChartForm/flows/netFlowsConfig";
import { VisualizationApiToolsButton } from "~/modules/visualization/components/VisualizationApiToolsButton";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";
import { ExportTableNames } from "~/modules/downloads/types";
import { buildNetFlowsQuery } from "~/modules/visualization/util/buildNetFlowsQuery";

// // // //

const selectFlowsQueryResponse = (data: unknown) => {
    return deobfuscate(data as string) as QueryResponse<AggregateItem>;
};

// TODO - pass in props.height

/**
 * NetFlowsChartRenderer
 * Renders a net flows chart for the configured assets and date range.
 */
export function NetFlowsChartProvider(props: {
    title: string;
    description: string;
    config: ChartConfig;
    children: (props: {
        title: string;
        description: string;
        isLoading: boolean;
        isEmpty: boolean;
        chartEl: React.ReactNode;
        emptyEl: React.ReactNode;
        loadingEl: React.ReactNode;
        customControls: React.ReactNode;
    }) => React.ReactNode;
}) {
    const { title, config } = props;

    const netFlowsConfig = useMemo(
        () => toNetFlowsChartConfig(config),
        [config],
    );

    const startDate = useMemo(
        () => startOfDay(new Date(netFlowsConfig.start_date)),
        [netFlowsConfig.start_date],
    );
    const endDate = useMemo(
        () => endOfDay(new Date(netFlowsConfig.end_date)),
        [netFlowsConfig.end_date],
    );

    const dateRangeLabel = useMemo(
        // () => `${format(startDate, "LLL d, yyyy")} – ${format(endDate, "LLL d, yyyy")}`,
        () =>
            `${format(startDate, "LLL d, yyyy")} to ${format(endDate, "LLL d, yyyy")}`,
        [startDate, endDate],
    );

    const flowsApiFilters = useMemo(
        () => getNetFlowsApiFilters(netFlowsConfig.query),
        [netFlowsConfig.query],
    );

    // Group-bys the viewer can switch between (viewer controls).
    const groupByOptions = netFlowsConfig.groups_by_options ?? [];

    // Default to the configured group-by, falling back to the viewer's first
    // option, then to the net flows default.
    const defaultGroupBy =
        netFlowsConfig.default_group_by ??
        groupByOptions[0] ??
        DEFAULT_NET_FLOWS_GROUP_BY;

    // Set the group-by to the default value.
    const [groupBy, setGroupBy] =
        useState<TokenTimeseriesGroupBy>(defaultGroupBy);

    // Reset the group-by to the default value when the default value changes.
    useEffect(() => {
        setGroupBy(defaultGroupBy);
    }, [defaultGroupBy]);

    const flowsFromDateReq = api.visualization.queryFlowsFromDate.useQuery(
        {
            filters: flowsApiFilters,
            groupBy,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            limit: NET_FLOWS_MAX_ASSETS,
        },
        {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            select: selectFlowsQueryResponse,
        },
    );

    // Build query for API Tools and Data Export
    const query = buildNetFlowsQuery({
        filters: flowsApiFilters,
        groupBy,
        startDate: startDate,
        endDate: endDate,
    });

    const isLoading = flowsFromDateReq.isFetching;
    const flowsFromDateItems = (flowsFromDateReq.data?.results ?? []).filter(
        (item) => Math.abs(item.value) >= netFlowsConfig.min_net_flow,
    );
    // const cardTitle = `${title || "Net Flows"} · ${dateRangeLabel}`;
    const cardTitle = `${title || "Net Flows"}`;
    const subtitle = `Net Flows from ${dateRangeLabel}. Net flows are calculated as the difference between tokens minted and tokens burned.${
        netFlowsConfig.min_net_flow > 0
            ? ` Showing assets with at least $${formatCurrencyValueCompact(netFlowsConfig.min_net_flow)} in absolute net flow.`
            : ""
    }. Last calculated ${format(new Date(), "LLL d, yyyy")}.`;

    // Only surface the group-by control when viewers have more than one option.
    const customControls = (
        <div className="flex items-center gap-3 justify-end self-start">
            {groupByOptions.length > 1 && (
                <>
                    <p className="text-sm text-foreground whitespace-nowrap select-none">
                        Group By
                    </p>
                    <SelectInput
                        name="net-flows--group-by-select"
                        value={groupBy}
                        size="sm"
                        disabled={isLoading}
                        onChange={(updatedValue) => {
                            setGroupBy(updatedValue as TokenTimeseriesGroupBy);
                        }}
                        options={groupByOptions.map((option) => ({
                            value: option,
                            content: {
                                label: TOKEN_TIMESERIES_GROUP_BY_LABELS[option],
                            },
                        }))}
                    />
                </>
            )}

            <VisualizationApiToolsButton
                query={query}
                apiDocsBaseUrl={
                    API_QUERY_BASE_URLS[ApiQueryEndpoints.TokenTimeseriesV4]
                }
                size="xs"
            />

            <DataExportProvider>
                {({ isLoading, exportCsv }) => (
                    <DownloadButton
                        disabled={isLoading}
                        onClick={() => {
                            exportCsv({
                                exportTableName:
                                    ExportTableNames.token_timeseries_v4,
                                query: query,
                            });
                        }}
                    />
                )}
            </DataExportProvider>
        </div>
    );

    const loadingElement = (
        <div className="flex h-[400px] items-center justify-center">
            <LogoLoadingSpinner size={75} />
        </div>
    );

    const chartEl = (
        <>
            <NetFlowsChart
                height={getChartHeight(flowsFromDateItems.length)}
                items={flowsFromDateItems}
                scale={netFlowsConfig.scale}
                animation={false}
                legendWidth={220}
                showIcon={true}
            />

            <p className="text-xs text-muted-foreground font-light flex items-center gap-2">
                {subtitle}
            </p>
        </>
    );

    const emptyEl = (
        <div
            className="flex flex-col items-center justify-center px-6 text-center"
            style={{
                height: getChartHeight(10),
            }}
        >
            <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <BarChart3 className="h-8 w-8" />
            </div>
            <h3 className="text-xl text-foreground">No net flows to show</h3>
            <p className="mt-2 max-w-md text-muted-foreground">
                No mint or burn activity was found for the selected assets from{" "}
                {dateRangeLabel}. Try a different date range or set of assets.
            </p>
        </div>
    );

    const isEmpty =
        !isLoading &&
        flowsFromDateItems.length === 0 &&
        flowsFromDateReq.isSuccess;

    return (
        <>
            {props.children({
                title,
                description: props.description,
                isLoading,
                isEmpty,
                chartEl,
                emptyEl,
                loadingEl: loadingElement,
                customControls,
            })}
        </>
    );

    //         {isLoading && (
    //         )}
    //         {!isLoading && flowsFromDateItems.length > 0 && (
    //         )}
    //         {!isLoading &&
    //             flowsFromDateItems.length === 0 &&
    //             flowsFromDateReq.isSuccess && (
    //                 <div
    //                     className="flex flex-col items-center justify-center px-6 text-center"
    //                     style={{
    //                         height: getChartHeight(10),
    //                     }}
    //                 >
    //                     <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
    //                         <BarChart3 className="h-8 w-8" />
    //                     </div>
    //                     <h3 className="text-xl text-foreground">
    //                         No net flows to show
    //                     </h3>
    //                     <p className="mt-2 max-w-md text-muted-foreground">
    //                         No mint or burn activity was found for the selected
    //                         assets from {dateRangeLabel}. Try a different date
    //                         range or set of assets.
    //                     </p>
    //                 </div>
    //             )}
    //     </VisualizationCard>
    // );
}
